/**
 * AbsencesPanel - Gestion des absences dans la Conduite
 * 
 * Fonctionnalités:
 * - Affichage linéaire et chronologique des absences
 * - Popup pour ajouter/modifier une absence
 * - Les amiraux peuvent gérer les absences de tout le monde
 * - Les autres utilisateurs ne peuvent gérer que leurs propres absences
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Plus, Trash2, Edit2, User, X, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Jours fériés en France (format MM-DD pour les fixes, calculés pour les mobiles)
const getJoursFeries = (year) => {
  const feries = [
    `${year}-01-01`, // Jour de l'an
    `${year}-05-01`, // Fête du travail
    `${year}-05-08`, // Victoire 1945
    `${year}-07-14`, // Fête nationale
    `${year}-08-15`, // Assomption
    `${year}-11-01`, // Toussaint
    `${year}-11-11`, // Armistice
    `${year}-12-25`, // Noël
  ];
  
  // Calcul de Pâques (algorithme de Oudin)
  const g = year % 19;
  const c = Math.floor(year / 100);
  const h = (c - Math.floor(c / 4) - Math.floor((8 * c + 13) / 25) + 19 * g + 15) % 30;
  const i = h - Math.floor(h / 28) * (1 - Math.floor(h / 28) * Math.floor(29 / (h + 1)) * Math.floor((21 - g) / 11));
  const j = (year + Math.floor(year / 4) + i + 2 - c + Math.floor(c / 4)) % 7;
  const l = i - j;
  const month = 3 + Math.floor((l + 40) / 44);
  const day = l + 28 - 31 * Math.floor(month / 4);
  
  const paques = new Date(year, month - 1, day);
  const lundiPaques = new Date(paques);
  lundiPaques.setDate(paques.getDate() + 1);
  const ascension = new Date(paques);
  ascension.setDate(paques.getDate() + 39);
  const lundiPentecote = new Date(paques);
  lundiPentecote.setDate(paques.getDate() + 50);
  
  feries.push(lundiPaques.toISOString().split('T')[0]); // Lundi de Pâques
  feries.push(ascension.toISOString().split('T')[0]); // Ascension
  feries.push(lundiPentecote.toISOString().split('T')[0]); // Lundi de Pentecôte
  
  return new Set(feries);
};

// Vérifier si une date est un jour ouvré en France
const isJourOuvre = (date, joursFeries) => {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  // 0 = dimanche, 6 = samedi
  if (dayOfWeek === 0 || dayOfWeek === 6) return false;
  // Vérifier si c'est un jour férié
  const dateStr = d.toISOString().split('T')[0];
  return !joursFeries.has(dateStr);
};

// Calculer les jours ouvrés qui se chevauchent entre deux périodes
const getJoursOuvresCommuns = (start1, end1, start2, end2) => {
  const overlapStart = new Date(Math.max(new Date(start1), new Date(start2)));
  const overlapEnd = new Date(Math.min(new Date(end1), new Date(end2)));
  
  if (overlapStart > overlapEnd) return [];
  
  const joursCommuns = [];
  const years = new Set([overlapStart.getFullYear(), overlapEnd.getFullYear()]);
  const joursFeries = new Set();
  years.forEach(y => getJoursFeries(y).forEach(f => joursFeries.add(f)));
  
  const current = new Date(overlapStart);
  while (current <= overlapEnd) {
    if (isJourOuvre(current, joursFeries)) {
      joursCommuns.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  
  return joursCommuns;
};

const AbsencesPanel = ({ currentUser, users = [], compact = false }) => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [expanded, setExpanded] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  const isAmiral = currentUser?.grade === 'amiral';
  
  // État pour le popup des chevauchements
  const [showOverlapPopup, setShowOverlapPopup] = useState(false);
  
  // État pour le popup de confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, absence: null });
  
  // Détecter les chevauchements d'absences (jours ouvrés uniquement)
  const detectOverlaps = (absencesList) => {
    const overlaps = []; // Liste des chevauchements avec détails
    
    for (let i = 0; i < absencesList.length; i++) {
      for (let j = i + 1; j < absencesList.length; j++) {
        const a = absencesList[i];
        const b = absencesList[j];
        
        // Calculer les jours ouvrés communs
        const joursCommuns = getJoursOuvresCommuns(
          a.start_date, a.end_date,
          b.start_date, b.end_date
        );
        
        if (joursCommuns.length > 0) {
          overlaps.push({
            absence1: a,
            absence2: b,
            joursCommuns: joursCommuns,
            count: joursCommuns.length
          });
        }
      }
    }
    
    return overlaps;
  };
  
  // Calculer les chevauchements
  const overlaps = useMemo(() => detectOverlaps(absences), [absences]);
  
  // Map pour savoir quelles absences ont des chevauchements
  const absencesWithOverlaps = useMemo(() => {
    const map = new Map();
    overlaps.forEach(o => {
      if (!map.has(o.absence1.id)) map.set(o.absence1.id, []);
      if (!map.has(o.absence2.id)) map.set(o.absence2.id, []);
      map.get(o.absence1.id).push({ with: o.absence2, days: o.count });
      map.get(o.absence2.id).push({ with: o.absence1, days: o.count });
    });
    return map;
  }, [overlaps]);
  
  // Nombre total de jours ouvrés en chevauchement
  const totalOverlapDays = useMemo(() => 
    overlaps.reduce((sum, o) => sum + o.count, 0), [overlaps]);
  
  // Charger les absences
  const loadAbsences = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/absences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAbsences(data.absences || []);
      }
    } catch (error) {
      console.error('Erreur chargement absences:', error);
    }
  };
  
  useEffect(() => {
    loadAbsences();
  }, []);
  
  // Ouvrir le modal pour créer
  const openCreateModal = () => {
    setEditingAbsence(null);
    setFormData({
      user_id: '',
      start_date: '',
      end_date: '',
      reason: ''
    });
    setShowModal(true);
  };
  
  // Ouvrir le modal pour éditer
  const openEditModal = (absence) => {
    setEditingAbsence(absence);
    setFormData({
      user_id: absence.user_id,
      start_date: absence.start_date,
      end_date: absence.end_date,
      reason: absence.reason || ''
    });
    setShowModal(true);
  };
  
  // Sauvegarder (créer ou modifier)
  const handleSave = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error('Veuillez remplir les dates');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = editingAbsence 
        ? `${API_URL}/api/absences/${editingAbsence.id}`
        : `${API_URL}/api/absences`;
      
      const method = editingAbsence ? 'PUT' : 'POST';
      
      // Pour la création, envoyer user_id seulement si différent de l'utilisateur courant
      const payload = editingAbsence 
        ? { start_date: formData.start_date, end_date: formData.end_date, reason: formData.reason }
        : { 
            start_date: formData.start_date, 
            end_date: formData.end_date, 
            reason: formData.reason,
            user_id: formData.user_id && formData.user_id !== currentUser?.id ? formData.user_id : null
          };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success(editingAbsence ? 'Absence modifiée' : 'Absence ajoutée');
        setShowModal(false);
        loadAbsences();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  // Demander confirmation avant suppression
  const askDeleteConfirmation = (absence) => {
    setDeleteConfirm({ show: true, absence });
  };
  
  // Supprimer une absence (après confirmation)
  const handleDelete = async () => {
    if (!deleteConfirm.absence) return;
    
    const absenceId = deleteConfirm.absence.id;
    setDeleteConfirm({ show: false, absence: null });
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/absences/${absenceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Absence supprimée');
        loadAbsences();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };
  
  // Formater la période
  const formatPeriod = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { day: 'numeric', month: 'short' };
    
    if (start === end) {
      return startDate.toLocaleDateString('fr-FR', options);
    }
    
    return `${startDate.toLocaleDateString('fr-FR', options)} → ${endDate.toLocaleDateString('fr-FR', options)}`;
  };
  
  // Vérifier si l'utilisateur peut modifier une absence
  const canEdit = (absence) => {
    return isAmiral || absence.user_id === currentUser?.id;
  };
  
  // Filtrer les absences à venir ou en cours
  const today = new Date().toISOString().split('T')[0];
  const relevantAbsences = absences.filter(a => a.end_date >= today);
  
  // Version compacte (inline) pour la barre de progression
  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2 text-slate-400 flex-wrap">
          <Calendar className="w-4 h-4 flex-shrink-0" />
          <span className="flex-shrink-0">Absences :</span>
          {relevantAbsences.length === 0 ? (
            <span className="text-slate-500">Aucune</span>
          ) : (
            relevantAbsences.map((absence, idx) => {
              const hasOverlap = absencesWithOverlaps.has(absence.id);
              const overlapInfo = absencesWithOverlaps.get(absence.id) || [];
              const totalDays = overlapInfo.reduce((sum, o) => sum + o.days, 0);
              
              return (
                <span 
                  key={absence.id} 
                  className={`flex items-center gap-1 ${hasOverlap ? 'text-orange-300' : 'text-slate-300'}`}
                  title={hasOverlap ? `⚠️ ${totalDays} jour(s) ouvré(s) en commun avec : ${overlapInfo.map(o => o.with.user_name).join(', ')}` : ''}
                >
                  {hasOverlap && <AlertTriangle className="w-3 h-3 text-orange-400" />}
                  <span className="font-medium">{absence.user_name?.split(' ')[0]}</span>
                  <span className={`text-xs ${hasOverlap ? 'text-orange-400' : 'text-violet-400'}`}>
                    {formatPeriod(absence.start_date, absence.end_date)}
                  </span>
                  {canEdit(absence) && (
                    <button 
                      onClick={() => askDeleteConfirmation(absence)}
                      className="text-slate-600 hover:text-red-400 ml-0.5"
                      title="Supprimer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  {idx < relevantAbsences.length - 1 && <span className="text-slate-600 ml-1">•</span>}
                </span>
              );
            })
          )}
          {overlaps.length > 0 && (
            <button 
              onClick={() => setShowOverlapPopup(true)}
              className="px-2 py-0.5 bg-red-200 text-xs flex items-center gap-1 rounded hover:bg-red-300 transition-colors cursor-pointer"
              title="Voir les détails des chevauchements"
            >
              <AlertTriangle className="w-3 h-3 text-red-700" />
              <span className="text-gray-900 font-medium">{overlaps.length} chevauchement{overlaps.length > 1 ? 's' : ''}</span>
            </button>
          )}
          <button 
            onClick={openCreateModal}
            className="px-2 py-0.5 text-xs text-slate-400 hover:text-violet-400 hover:bg-violet-500/10 rounded transition-colors flex-shrink-0 flex items-center gap-1"
            title="Ajouter une absence"
          >
            <Plus className="w-3 h-3" />
            <span>Ajouter une absence</span>
          </button>
        </div>
        
        {/* Popup des chevauchements */}
        {showOverlapPopup && (
          <OverlapPopup 
            overlaps={overlaps} 
            onClose={() => setShowOverlapPopup(false)} 
          />
        )}
        
        {/* Modal */}
        {showModal && <AbsenceModal 
          formData={formData} 
          setFormData={setFormData} 
          editingAbsence={editingAbsence}
          isAmiral={isAmiral}
          currentUser={currentUser}
          users={users}
          loading={loading}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
          existingAbsences={absences}
        />}
        
        {/* Popup de confirmation de suppression */}
        {deleteConfirm.show && (
          <DeleteConfirmPopup 
            absence={deleteConfirm.absence}
            onConfirm={handleDelete}
            onCancel={() => setDeleteConfirm({ show: false, absence: null })}
            formatPeriod={formatPeriod}
          />
        )}
      </>
    );
  }
  
  // Version complète (card)
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-semibold text-slate-200">Absences</h3>
          {relevantAbsences.length > 0 && (
            <span className="text-[10px] text-slate-500">({relevantAbsences.length})</span>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
          onClick={openCreateModal}
          data-testid="add-absence-btn"
        >
          <Plus className="w-3 h-3 mr-1" />
          Ajouter
        </Button>
      </div>
      
      {/* Liste des absences */}
      {relevantAbsences.length === 0 ? (
        <p className="text-xs text-slate-500 italic">Aucune absence planifiée</p>
      ) : (
        <div className="space-y-1.5">
          {relevantAbsences.map(absence => (
            <div 
              key={absence.id}
              className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-800/50 rounded-md border border-slate-700/50 group"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <User className="w-3 h-3 text-slate-500 flex-shrink-0" />
                <span className="text-xs font-medium text-slate-300 truncate">
                  {absence.user_name}
                </span>
                <span className="text-[10px] text-violet-400 whitespace-nowrap">
                  {formatPeriod(absence.start_date, absence.end_date)}
                </span>
                {absence.reason && (
                  <span className="text-[10px] text-slate-500 truncate hidden sm:inline">
                    ({absence.reason})
                  </span>
                )}
              </div>
              
              {canEdit(absence) && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEditModal(absence)}
                    className="p-1 text-slate-500 hover:text-slate-300 rounded"
                    title="Modifier"
                  >
                    <Edit2 className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => askDeleteConfirmation(absence)}
                    className="p-1 text-slate-500 hover:text-red-400 rounded"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Modal */}
      {showModal && <AbsenceModal 
        formData={formData} 
        setFormData={setFormData} 
        editingAbsence={editingAbsence}
        isAmiral={isAmiral}
        currentUser={currentUser}
        users={users}
        loading={loading}
        onSave={handleSave}
        onClose={() => setShowModal(false)}
        existingAbsences={absences}
      />}
      
      {/* Popup des chevauchements (version carte) */}
      {showOverlapPopup && (
        <OverlapPopup 
          overlaps={overlaps} 
          onClose={() => setShowOverlapPopup(false)} 
        />
      )}
      
      {/* Popup de confirmation de suppression */}
      {deleteConfirm.show && (
        <DeleteConfirmPopup 
          absence={deleteConfirm.absence}
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm({ show: false, absence: null })}
          formatPeriod={formatPeriod}
        />
      )}
    </div>
  );
};

// Composant Popup de confirmation de suppression
const DeleteConfirmPopup = ({ absence, onConfirm, onCancel, formatPeriod }) => {
  if (!absence) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-red-500/10">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-400" />
            <h3 className="text-sm font-semibold text-red-300">
              Confirmer la suppression
            </h3>
          </div>
          <button 
            onClick={onCancel}
            className="p-1 text-slate-500 hover:text-slate-300 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4">
          <p className="text-sm text-slate-300 mb-3">
            Voulez-vous vraiment supprimer cette absence ?
          </p>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-slate-400" />
              <span className="font-medium text-slate-200">{absence.user_name}</span>
            </div>
            <div className="text-sm text-violet-400">
              {formatPeriod(absence.start_date, absence.end_date)}
            </div>
            {absence.reason && (
              <div className="text-xs text-slate-500 mt-1">
                Motif : {absence.reason}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-200"
          >
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-500 text-white"
          >
            Supprimer
          </Button>
        </div>
      </div>
    </div>
  );
};

// Composant Popup pour afficher les détails des chevauchements
const OverlapPopup = ({ overlaps, onClose }) => {
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-orange-500/10">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            <h3 className="text-sm font-semibold text-orange-300">
              Chevauchements d'absences ({overlaps.length})
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-300 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <p className="text-xs text-slate-400 mb-4">
            Seuls les <span className="text-orange-400 font-medium">jours ouvrés</span> (lundi-vendredi, hors jours fériés) sont comptabilisés.
          </p>
          
          {overlaps.map((overlap, idx) => (
            <div 
              key={idx}
              className="bg-slate-800/50 border border-orange-500/30 rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-orange-400 font-bold text-lg">{overlap.count}</span>
                  <span className="text-xs text-slate-400">jour{overlap.count > 1 ? 's' : ''} ouvré{overlap.count > 1 ? 's' : ''}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-slate-900/50 rounded p-2">
                  <div className="font-medium text-slate-200">{overlap.absence1.user_name}</div>
                  <div className="text-violet-400">
                    {formatDate(overlap.absence1.start_date)} → {formatDate(overlap.absence1.end_date)}
                  </div>
                  {overlap.absence1.reason && (
                    <div className="text-slate-500 mt-1">({overlap.absence1.reason})</div>
                  )}
                </div>
                <div className="bg-slate-900/50 rounded p-2">
                  <div className="font-medium text-slate-200">{overlap.absence2.user_name}</div>
                  <div className="text-violet-400">
                    {formatDate(overlap.absence2.start_date)} → {formatDate(overlap.absence2.end_date)}
                  </div>
                  {overlap.absence2.reason && (
                    <div className="text-slate-500 mt-1">({overlap.absence2.reason})</div>
                  )}
                </div>
              </div>
              
              {/* Liste des jours en commun */}
              <div className="mt-2 pt-2 border-t border-slate-700">
                <div className="text-[10px] text-slate-500 mb-1">Jours ouvrés en commun :</div>
                <div className="flex flex-wrap gap-1">
                  {overlap.joursCommuns.slice(0, 10).map((jour, i) => (
                    <span 
                      key={i}
                      className="px-1.5 py-0.5 bg-orange-500/20 text-orange-300 rounded text-[10px]"
                    >
                      {jour.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                  ))}
                  {overlap.joursCommuns.length > 10 && (
                    <span className="px-1.5 py-0.5 text-slate-500 text-[10px]">
                      +{overlap.joursCommuns.length - 10} autres
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          
          {overlaps.length === 0 && (
            <p className="text-center text-slate-500 py-4">Aucun chevauchement de jours ouvrés</p>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-700">
          <Button
            size="sm"
            onClick={onClose}
            className="w-full bg-slate-700 hover:bg-slate-600 text-white"
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

// Composant Modal séparé pour réutilisation
const AbsenceModal = ({ formData, setFormData, editingAbsence, isAmiral, currentUser, users, loading, onSave, onClose, existingAbsences = [] }) => {
  // Détecter si la nouvelle absence chevauche une existante
  const checkOverlap = () => {
    if (!formData.start_date || !formData.end_date) return null;
    
    const newStart = new Date(formData.start_date);
    const newEnd = new Date(formData.end_date);
    const targetUserId = formData.user_id || currentUser?.id;
    
    const overlapping = existingAbsences.filter(a => {
      // Ne pas comparer avec soi-même en édition
      if (editingAbsence && a.id === editingAbsence.id) return false;
      
      const aStart = new Date(a.start_date);
      const aEnd = new Date(a.end_date);
      
      // Chevauchement si les périodes se croisent
      return newStart <= aEnd && newEnd >= aStart;
    });
    
    return overlapping.length > 0 ? overlapping : null;
  };
  
  const overlappingAbsences = checkOverlap();
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <h3 className="text-sm font-semibold text-slate-200">
            {editingAbsence ? 'Modifier l\'absence' : 'Nouvelle absence'}
          </h3>
          <button 
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-300 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Modal Body */}
        <div className="p-4 space-y-4">
          {/* Alerte chevauchement */}
          {overlappingAbsences && (
            <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-orange-300">
                <span className="font-medium">Attention :</span> Cette période chevauche {overlappingAbsences.length} absence(s) existante(s) :
                <ul className="mt-1 ml-2">
                  {overlappingAbsences.slice(0, 3).map(a => (
                    <li key={a.id}>• {a.user_name} ({a.start_date} → {a.end_date})</li>
                  ))}
                  {overlappingAbsences.length > 3 && <li>• ... et {overlappingAbsences.length - 3} autre(s)</li>}
                </ul>
              </div>
            </div>
          )}
          
          {/* Sélection utilisateur (amiraux uniquement, création uniquement) */}
          {isAmiral && !editingAbsence && (
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">
                Utilisateur
              </label>
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500"
              >
                <option value="">Moi-même</option>
                {users.filter(u => u.id !== currentUser?.id).map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.email}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Date de début */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Date de début
            </label>
            <input
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500"
            />
          </div>
          
          {/* Date de fin */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Date de fin
            </label>
            <input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              min={formData.start_date}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-violet-500"
            />
          </div>
          
          {/* Motif */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Motif <span className="text-slate-600">(optionnel)</span>
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Congés, formation, maladie..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-violet-500"
            />
          </div>
        </div>
        
        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={loading}
            className={overlappingAbsences ? "bg-orange-600 hover:bg-orange-500 text-white" : "bg-violet-600 hover:bg-violet-500 text-white"}
          >
            {loading ? 'Enregistrement...' : (editingAbsence ? 'Modifier' : (overlappingAbsences ? 'Ajouter quand même' : 'Ajouter'))}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AbsencesPanel;
